import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { Loader2, RotateCcw, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { useGetFedexRating } from "@/api/rating.api";
import {
  CreateRatingFormSchema,
  CreateRatingFormData,
} from "@/components/backOffice/ratings/rating.form";
import { FedexRatingResponseDto } from "@/types/api/api-types";
import { RatingAddressFields } from "./RatingAddressFields";
import { RatingPackageFields } from "./RatingPackageFields";
import { RatingResultCard } from "./RatingResultCard";
import { useEffect, useState } from "react";
import { PELICAN_AIR_SERVICES_ADDRESS } from "@/constants/pelicanAirServices";

export const RatingForm = () => {
  const { t } = useTranslation("boRatings");
  const { mutateAsync: getFedexRating, isPending: isFedexPending } = useGetFedexRating();
  const [ratingResult, setRatingResult] = useState<FedexRatingResponseDto | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (ratingResult) setPanelOpen(true);
  }, [ratingResult]);

  const { control, handleSubmit, setValue, reset } = useForm<CreateRatingFormData>({
    resolver: zodResolver(CreateRatingFormSchema),
    defaultValues: {
      shipper: { ...PELICAN_AIR_SERVICES_ADDRESS },
      recipient: {
        streetLines: [],
        city: "",
        postalCode: "",
        countryCode: "",
        stateOrProvinceCode: "",
      },
      totalDeclaredValue: undefined,
      totalWeight: undefined,
      packages: [
        {
          weight: undefined,
          length: undefined,
          width: undefined,
          height: undefined,
          value: undefined,
        },
      ],
    },
  });

  const handleReset = () => {
    reset();
    setRatingResult(null);
    setPanelOpen(false);
  };

  const onSubmitFedex = async (data: CreateRatingFormData) => {
    try {
      setRatingResult(null);
      setPanelOpen(false);
      const response = await getFedexRating({
        shipper: {
          streetLine1: data.shipper.streetLines?.[0] || "",
          streetLine2: data.shipper.streetLines?.[1],
          city: data.shipper.city || "",
          postalCode: data.shipper.postalCode || "",
          countryCode: data.shipper.countryCode,
          stateOrProvinceCode: data.shipper.stateOrProvinceCode,
        },
        recipient: {
          streetLine1: data.recipient.streetLines?.[0] || "",
          streetLine2: data.recipient.streetLines?.[1],
          city: data.recipient.city || "",
          postalCode: data.recipient.postalCode || "",
          countryCode: data.recipient.countryCode,
          stateOrProvinceCode: data.recipient.stateOrProvinceCode,
        },
        totalDeclaredValue: data.totalDeclaredValue,
        totalWeight: data.totalWeight,
        packages: data.packages,
      });
      setRatingResult(response);
    } catch (error) {
      const description =
        isAxiosError(error) && typeof error.response?.data?.message === "string"
          ? error.response.data.message
          : null;

      toast.error(
        description ? (
          <div className="max-w-sm break-words">
            <p className="font-semibold">{t("ratingForm.fedexRatingError")}</p>
            <p className="mt-1 text-sm leading-snug font-normal">{description}</p>
          </div>
        ) : (
          t("ratingForm.fedexRatingError")
        ),
        { className: description ? "!max-w-sm !w-full" : undefined },
      );
    }
  };

  return (
    <APIProvider apiKey="AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw">
      <div className="flex flex-col gap-6">
        <form className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <RatingAddressFields
              control={control}
              setValue={setValue}
              prefix="shipper"
              title={t("ratingForm.shipperTitle")}
            />
            <RatingAddressFields
              control={control}
              setValue={setValue}
              prefix="recipient"
              title={t("ratingForm.recipientTitle")}
            />
          </div>

          <RatingPackageFields control={control} setValue={setValue} />

          <div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-6">
            <ControlledUnifiedInput
              control={control}
              name="totalDeclaredValue"
              label={t("ratingForm.totalDeclaredValueLabel")}
              inputType="float"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={isFedexPending}
              className="w-fit"
              onClick={handleSubmit(onSubmitFedex)}
            >
              {isFedexPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("ratingForm.submitting")}
                </>
              ) : (
                t("ratingForm.submit")
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isFedexPending}
              className="w-fit"
              onClick={handleReset}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t("ratingForm.reset")}
            </Button>
          </div>
        </form>

        {ratingResult && (
          <div className="@container hidden w-full lg:block">
            <RatingResultCard data={ratingResult} readOnly />
          </div>
        )}

        {panelOpen && ratingResult && (
          <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="text-lg font-semibold text-green-800">{t("resultPanel.title")}</h2>
                <p className="text-sm text-gray-500">{t("resultPanel.description")}</p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => setPanelOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="@container p-4">
                <RatingResultCard data={ratingResult} readOnly />
              </div>
            </ScrollArea>
          </div>
        )}

        {ratingResult && !panelOpen && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setPanelOpen(true)}
            className="w-fit gap-2 lg:hidden"
          >
            {t("ratingForm.showResult")}
          </Button>
        )}
      </div>
    </APIProvider>
  );
};
